import { memo } from "react";
import companyLogo from "@/assets/ulture-logo-tile.png";
import { cn } from "@/lib/utils";

type Size = "sm" | "md" | "lg";
type Tone = "light" | "dark";

interface Props {
  size?: Size;
  tone?: Tone;
  subtitle?: string;
  className?: string;
  /** Hide the wordmark, render only the app-icon tile. */
  iconOnly?: boolean;
}

const sizeMap: Record<Size, { tile: string; img: string; word: string; sub: string; gap: string }> = {
  sm: { tile: "w-9 h-9 rounded-[10px]", img: "w-7 h-7", word: "text-base", sub: "text-[10px]", gap: "gap-2" },
  md: { tile: "w-12 h-12 rounded-[14px]", img: "w-10 h-10", word: "text-lg", sub: "text-xs", gap: "gap-2.5" },
  lg: { tile: "w-20 h-20 rounded-[22px]", img: "w-16 h-16", word: "text-3xl", sub: "text-sm", gap: "gap-4" },
};

/**
 * Shared brand mark for the pre-login experience.
 * Renders the existing Ulture "U" logo on a white app-icon-style rounded
 * tile, optionally followed by the "Ulture Pest" wordmark and a subtitle.
 */
export const UltureAppMark = memo(function UltureAppMark({
  size = "md",
  tone = "light",
  subtitle,
  className,
  iconOnly = false,
}: Props) {
  const s = sizeMap[size];
  const wordColor = tone === "light" ? "text-white" : "text-[#2cb7c3]";
  const subColor = tone === "light" ? "text-white/75" : "text-foreground/70";

  return (
    <div className={cn("flex items-center", s.gap, className)}>
      <div
        className={cn(
          s.tile,
          "bg-white flex items-center justify-center shrink-0",
          "shadow-[0_6px_20px_-6px_rgba(15,42,77,0.35)]",
          "ring-1 ring-black/5"
        )}
      >
        <img src={companyLogo} alt="Ulture Pest" className={cn(s.img, "object-contain")} />
      </div>
      {!iconOnly && (
        <div className="flex flex-col leading-tight">
          <span className={cn("font-[700] tracking-tight", s.word, wordColor)}>Ulture Pest</span>
          {subtitle && <span className={cn(s.sub, subColor, "font-medium")}>{subtitle}</span>}
        </div>
      )}
    </div>
  );
});

export default UltureAppMark;
